/**
 * stockCatalog.ts — Acervo compartilhado de imagens do Studio (lado servidor)
 *
 * ATENÇÃO: espelho de `src/lib/studio/core/assets/stockCatalog.ts`.
 * Ao mudar aqui, mudar lá — é o que garante que a prévia, a página publicada,
 * o PDF e o e-mail resolvam a mesma referência do mesmo jeito.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, acervo compartilhado — Fase 1)
 */

/** Prefixo usado nas referências gravadas nos blocos. */
export const STOCK_PREFIX = 'acervo://';

/** Monta o endereço de uma foto do Pexels na largura pedida. */
function pexels(id: number, largura: number): string {
  return `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${largura}`;
}

/** Identificadores das fotos da carga inicial (mesma lista da tabela). */
const IDS: Record<string, number> = {
  'capa.cidade-noite': 380769,
  'capa.reuniao': 3184291,
  'capa.equipe': 3184465,
  'capa.escritorio': 3182812,
  'capa.notebook': 3861969,
  'capa.painel': 590041,
  'capa.mesa-trabalho': 265087,
  'capa.atendimento': 1181244,
  'capa.arquitetura': 325185,
  'capa.palestra': 2887661,
  'capa.produto': 1092644,
  'capa.loja': 3184405,
  'capa.celular': 5926382,
  'capa.criativo': 1595385,
  'capa.industria': 4348404,
  'capa.consultoria': 3184418,
  'capa.dados': 3183150,
  'capa.interior': 1103970,
  'capa.time': 3182746,
  'capa.laptop-codigo': 4386370,
  'capa.retrato': 936137,
  'capa.minimal': 3760067,
  'capa.logistica': 7688336,
};

/** Cópia de segurança embutida: chave → endereço da imagem. */
const EMBUTIDO: Record<string, string> = Object.fromEntries(
  Object.entries(IDS).map(([chave, id]) => [chave, pexels(id, 1600)]),
);

/** Catálogo carregado do banco pela Edge Function que estiver compilando. */
let catalogo: Record<string, string> = {};

/** Carrega/atualiza o catálogo vindo do banco. */
export function setStockCatalog(itens: Record<string, string>): void {
  catalogo = { ...itens };
}

/** Transforma o valor de um campo de imagem no endereço final. */
export function resolveAssetUrl(valor: unknown): string {
  if (typeof valor !== 'string' || !valor) return '';
  if (!valor.startsWith(STOCK_PREFIX)) return valor;

  const chave = valor.slice(STOCK_PREFIX.length);
  return catalogo[chave] ?? EMBUTIDO[chave] ?? '';
}

/**
 * Carrega o catálogo ativo do banco e o deixa pronto para `resolveAssetUrl`.
 * Recebe um cliente Supabase já criado pela função que está compilando —
 * nunca cria conexão nem usa endereço fixo.
 */
// deno-lint-ignore no-explicit-any
export async function loadStockCatalog(cliente: any): Promise<void> {
  try {
    const { data, error } = await cliente
      .from('studio_stock_assets')
      .select('asset_key, image_url')
      .eq('is_active', true);

    if (error || !Array.isArray(data)) return;

    const mapa: Record<string, string> = {};
    for (const item of data) {
      if (item?.asset_key && item?.image_url) mapa[item.asset_key] = item.image_url;
    }
    setStockCatalog(mapa);
  } catch (_e) {
    // Falha ao carregar não pode derrubar a publicação: a cópia embutida assume.
  }
}
